import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { hasAccess } from '../lib/permissions';
import { SIDEBAR, AUTH } from '../constants/testIds';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  FileText,
  Calendar,
  Plane,
  CreditCard,
  MessageCircle,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Wind,
  Wallet,
  Map,
  MapPin,
  Image,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const allNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: SIDEBAR.dashboard, module: 'dashboard' },
  { path: '/leads', label: 'Leads', icon: Users, testId: SIDEBAR.leads, module: 'leads' },
  { path: '/customers', label: 'Customers', icon: UserCircle, testId: SIDEBAR.customers, module: 'customers' },
  { path: '/quotations', label: 'Quotations', icon: FileText, testId: SIDEBAR.quotations, module: 'quotations' },
  { path: '/bookings', label: 'Bookings', icon: Calendar, testId: SIDEBAR.bookings, module: 'bookings' },
  { path: '/itineraries', label: 'Itineraries', icon: Map, testId: SIDEBAR.itineraries, module: 'itineraries' },
  { path: '/trips', label: 'Trips', icon: MapPin, testId: 'sidebar-trips', module: 'trips' },
  { path: '/gallery', label: 'Gallery', icon: Image, testId: 'sidebar-gallery', module: 'gallery' },
  { path: '/visa', label: 'Visa & Passport', icon: Plane, testId: SIDEBAR.visa, module: 'visa' },
  { path: '/payments', label: 'Payments', icon: CreditCard, testId: SIDEBAR.payments, module: 'payments' },
  { path: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, testId: SIDEBAR.whatsapp, module: 'whatsapp' },
  { path: '/reports', label: 'Reports', icon: BarChart3, testId: SIDEBAR.reports, module: 'reports' },
  { path: '/accounts', label: 'Accounts', icon: Wallet, testId: SIDEBAR.accounts, module: 'accounts' },
  { path: '/users', label: 'Users', icon: Settings, testId: SIDEBAR.users, module: 'users' },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => hasAccess(user?.role, item.module));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        data-testid={SIDEBAR.nav}
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#383838' }}>
                <Wind className="w-5 h-5 text-white" />
              </div>
              <h1 className="font-outfit text-xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>
                Kites Fly
              </h1>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-secondary rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    data-testid={item.testId}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'text-white'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                      }`
                    }
                    style={({ isActive }) => isActive ? { backgroundColor: '#84bd0e' } : {}}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: '#383838' }}>
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <button 
            className="lg:hidden p-2 hover:bg-secondary rounded-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-muted-foreground" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <ThemeToggle />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                    <UserCircle className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="hidden sm:inline text-sm text-foreground">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  data-testid={AUTH.logoutBtn}
                  onClick={handleLogout}
                  className="text-destructive cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
